import * as React from 'react'
import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { Hint } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { useDiscoveryModalVideos } from 'modules/job_catalog/hooks/useDiscoveryModalVideos'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  onClose: () => void
  handleOnComplete: () => void
}

interface TrackingEventsMap {
  [key: string]: string
}

const onStepChange = (key: string) => {
  const trackingStepsEventsMap: TrackingEventsMap = {
    competencies: trackingEvents.discoveryModal.firstStepCompetenciesDiscoveryModal,
    performance: trackingEvents.discoveryModal.secondStepCompetenciesDiscoveryModal,
    jobs: trackingEvents.discoveryModal.thirdStepCompetenciesDiscoveryModal,
  }

  const trackingEvent = trackingStepsEventsMap[key]
  if (trackingEvent) {
    track(trackingEvent)
  }
}

const CompetenciesDiscoveryModal = ({ onClose, handleOnComplete }: Props) => {
  const { navigation } = useNavigation()
  const { hideJobsCatalogPopup } = useContext(EnvironmentContext)
  const navigate = useNavigate()
  const [videos, loading, error] = useDiscoveryModalVideos(i18n.locale)

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const canManageCompetencies = users.current.policy.can('competencies.competenciesManage')

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job'),
      ]),
    []
  )

  const actionLabel = () => {
    const id = firstEditableRol?.id

    return (id && canCreateJob) || (id && canManageCompetencies)
      ? i18n.t('discovery_modal.check_it_out')
      : i18n.t('discovery_modal.got_it')
  }

  const checkItOutAction = (id: number) => {
    track(trackingEvents.discoveryModal.clickCheckItOutCompetenciesDiscoveryModal)
    navigate(navigation.employees.jobCatalog.role.settings.fullPath({ id: id }))
  }

  const gotItAction = () => {
    track(trackingEvents.discoveryModal.clickGotItCompetenciesDiscoveryModal)
    onClose()
  }

  const finishAction = () => {
    const id = firstEditableRol?.id

    handleOnComplete()
    id && (canCreateJob || canManageCompetencies) ? checkItOutAction(id) : gotItAction()
  }

  const firstEditableRol = useMemo(() => {
    const roles = jobRolesCollection.filter((x) => !x.attributes.archived)
    const editableRoles = roles.filter((x) => policyAuthorizations.for('job_catalog.edit_job', x))

    return orderBy(editableRoles, 'employeesQuantity', ['desc'])[0]
  }, [policyAuthorizations.length])

  const url_competencies = videos?.[0]?.url
  const url_performance = videos?.[1]?.url
  const url_jobs = videos?.[2]?.url

  const steps = useMemo(() => {
    const steps = []
    if (url_competencies !== undefined)
      steps.push({
        key: 'competencies',
        src: url_competencies,
        title: i18n.t('discovery_modal.competencies_title'),
        description: i18n.t('discovery_modal.competencies_description'),
      })
    if (url_performance !== undefined)
      steps.push({
        key: 'performance',
        src: url_performance,
        title: i18n.t('discovery_modal.performance_review_title'),
        description: i18n.t('discovery_modal.performance_review_description'),
      })
    if (url_jobs !== undefined)
      steps.push({
        key: 'jobs',
        src: url_jobs,
        title: i18n.t('discovery_modal.jobs_title'),
        description: i18n.t('discovery_modal.jobs_description'),
      })

    if (steps.length === 0)
      steps.push({
        key: 'videos_missing',
        src: '',
        title: i18n.t('discovery_modal.videos_not_loaded_title'),
        description: i18n.t('discovery_modal.videos_not_loaded_description'),
      })
    return steps
  }, [url_competencies, url_performance, url_jobs])

  if (loading || error || videos === undefined || hideJobsCatalogPopup) return null

  return (
    <Hint.Modal
      onClose={onClose}
      onFinish={finishAction}
      actionLabel={actionLabel()}
      nextLabel={i18n.t('common.next')}
      onChange={onStepChange}
    >
      {steps.map(({ key, src, title, description }) => (
        <Hint.Modal.VideoStep
          id={key}
          key={key}
          src={src}
          title={title}
          description={description}
        />
      ))}
    </Hint.Modal>
  )
}

export default observer(CompetenciesDiscoveryModal)
